import React from "react";
import { useParams } from "react-router-dom";

import { MessageInterface, ApiHelper, PendingCommand, TemplateInterface, QuestionInterface, AnswerInterface, ArrayHelper, EnvironmentHelper, ToastHelper } from "../helpers"
import { Alert } from "react-bootstrap"
import { MessageEdit, MessagePreview } from "./components";
import { ImageEditor } from "../components";
import { Dots } from "react-activity";

interface Props {
  refreshSidebar: () => void,
}

export const MessagePage = (props: Props) => {
  const { id } = useParams<{ id: string }>();

  const [message, setMessage] = React.useState<MessageInterface>(null);
  const [editCommand, setEditCommand] = React.useState<PendingCommand>(null);
  const [template, setTemplate] = React.useState<TemplateInterface>(null);
  const [imageEditData, setImageEditData] = React.useState<{ question: QuestionInterface, answer: AnswerInterface, parentAnswer?: AnswerInterface, parentQuestion?: QuestionInterface }>(null);
  const [videoStatus, setVideoStatus] = React.useState("");
  const [refreshVideoStatus, setRefreshVideoStatus] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(true);

  const init = () => {
    setVideoStatus("");
    setIsLoading(true);
    if (id === "0") {
      const params = new URLSearchParams(window.location.search);
      const templateId = parseInt(params.get("templateId") || "0", 10);
      let shape = params.get("shape");
      if (!shape) shape = "16:9"
      setMessage({ id: 0, templateId: templateId, shape: shape, seconds: template?.seconds || 15, category: "", name: "" });
    }
    else ApiHelper.get<MessageInterface>("/messages/" + id)
      .then(data => { setMessage(data); })
      .catch(() => {
        ToastHelper.error("Failed to load message");
      });
    resetPage();
  }


  const resetPage = () => {
    if (imageEditData) setImageEditData(null);
    if (editCommand) setEditCommand(null);
    if (videoStatus) setVideoStatus("");
  }


  const loadTemplate = () => {
    if (message?.templateId > 0) ApiHelper.get<TemplateInterface>("/templates/" + message.templateId)
      .then(data => {
        setTemplate(data);
        if (message?.id === 0 && data) message.seconds = data.seconds;
      })
      .catch(() => { /* template load failed silently */ });
  }

  const updatePreview = async (questions: QuestionInterface[], answers: AnswerInterface[]) => {

    let previewMessage = message;
    let previewAnswers = answers;
    let previewQuestions = questions;
    let pointsToId = 0;

    questions?.forEach(q => {
      if (q.fieldType === "message") {
        const answer = ArrayHelper.getOne(answers, "questionId", q.id);
        if (answer) pointsToId = parseInt(answer.value, 0);
      }
    });

    if (pointsToId > 0) {
      previewMessage = await ApiHelper.getAnonymous<MessageInterface>("/messages/" + pointsToId);
      previewQuestions = await ApiHelper.getAnonymous<QuestionInterface[]>("/questions/template/" + previewMessage.templateId);
      previewAnswers = await ApiHelper.getAnonymous<AnswerInterface[]>("/answers/message/" + previewMessage.id);
    }


    const urlParams = new URLSearchParams(window.location.search);

    const answerArray: any[] = [];
    previewQuestions?.forEach(q => {
      const answer = ArrayHelper.getOne(previewAnswers, "questionId", q.id);
      let value = answer?.value;
      if (value && urlParams.get("shape") === "9:16") value = value.replace("/default.json", "/defaultV.json?v=2");
      answerArray.push({ keyName: q.keyName, value: (answer?.value) ? value : q.placeholder });


      if (q.keyName === "image" && (q.templateId === 1 || q.templateId === 13)) {
        // Check if this is vertical (9:16) - don't auto-open for vertical images
        let isVertical = false;
        if (q.choices) {
          try {
            const parts = q.choices.split(",");
            const aspectRatio = parseFloat(parts[0]) / parseFloat(parts[1]);
            isVertical = aspectRatio < 1; // Width < Height means vertical
          } catch (e) {
            console.error("Error checking aspect ratio for auto-open:", e);
          }
        }

        // Only auto-open for horizontal images
        if (!isVertical) {
          const cmd: PendingCommand = {
            action: "editImage",
            parameter: { question: q, answer: answer, isDefaultRendared: true }
          }
          handleMessageEditCommand(cmd);
        }
      }
    });
    const previewData = { name: "Preview", templateId: previewMessage?.templateId, seconds: 60, answers: answerArray };
    const cmd = { action: "data", value: JSON.stringify(previewData) };
    const previewFrame = document.getElementById('previewFrame') as HTMLIFrameElement | null;

    if (previewFrame && previewFrame.contentWindow && EnvironmentHelper.PlayerRoot) {
      previewFrame.contentWindow.postMessage(JSON.stringify(cmd), EnvironmentHelper.PlayerRoot);
    }
  }

  const handleMessageEditCommand = (cmd: PendingCommand) => {
    if (cmd.action === "updatePreview") {
      const param = cmd.parameter as { questions: QuestionInterface[]; answers: AnswerInterface[] };
      updatePreview(param.questions, param.answers);
    } else if (cmd.action === "editImage") {
      // Always open the editor when this command is received (auto-open prevention happens at the source)
      setImageEditData(cmd.parameter as { question: QuestionInterface; answer: AnswerInterface; parentAnswer?: AnswerInterface; parentQuestion?: QuestionInterface });
    } else if (cmd.action === "answerLoaded") {
      setIsLoading(false);
    }
  }

  const handleImageUpdate = (dataUrl: string) => {
    if (dataUrl) {
      let answer: AnswerInterface = { ...imageEditData.answer }
      answer.value = dataUrl;
      if (imageEditData.parentAnswer) setEditCommand({ action: "updateArrayAnswer", parameter: { answer, question: imageEditData.question, parentAnswer: imageEditData.parentAnswer } });
      else setEditCommand({ action: "updateAnswer", parameter: answer });
    }
    setImageEditData(null);
  }

  const getImageEditor = () => {
    if (imageEditData) {
      let aspectRatio = 16 / 9;
      try {
        if (imageEditData.question.choices) {
          const parts = imageEditData.question.choices.split(",");
          aspectRatio = parseFloat(parts[0]) / parseFloat(parts[1]);
        }
      } catch {
        // Use default aspect ratio if parsing fails
      }
      return <ImageEditor startingUrl={imageEditData.answer.value} updatedFunction={handleImageUpdate} name="imageEditor" aspectRatio={aspectRatio} />
    }
  }

  const getVideoStatus = () => {
    if (videoStatus) {
      // MediaConvert job statuses: SUBMITTED, PROGRESSING, COMPLETE, ERROR, CANCELED
      const status = videoStatus.toUpperCase();

      if (status === "COMPLETE") return <></>
      if (status === "SUBMITTED") return <Alert variant="info"><b>Submitted</b> - Your video has been successfully uploaded and is awaiting processing. It is safe to leave this page.</Alert>
      else if (status === "PROGRESSING") return <Alert variant="info">
        Video Status: <b>Progressing</b>
        <p style={{ marginTop: 10 }}>Your video has been successfully uploaded and is currently processing. This step typically takes about 3 minutes for each minute of video uploaded. It is safe to leave this page.</p>
        <div className="text-center"><Dots speed={0.4} animating={true} size={20} /></div>
      </Alert>
      else if (status === "CANCELED" || status === "CANCELLED") return <Alert variant="warning">
        Video Status: <b>Canceled</b>
        <p style={{ marginTop: 10 }}>This video processing job was canceled. Please try uploading your video again.</p>
      </Alert>
      else return <Alert variant="danger">
        Video Status: <b>{videoStatus}</b>
        <p style={{ marginTop: 10 }}>It appears your video failed to process. Please ensure you are uploading a valid mp4 and review the instructions on <a href="https://support.signpresenter.com/topics/uploadvideos.html" target="_blank" rel="noreferrer">this page</a> or contact support if this problem recurs.</p>
      </Alert>
    }
  }

  const updateVideoStatus = () => {
    let status = "";
    if (message?.awsJobId) {
      ApiHelper.get<{ status: string }>("/messages/jobStatus/" + message?.id)
        .then(data => {
          status = data?.status || "";
          setVideoStatus(data?.status || "")
          // Poll for status updates while job is in progress (MediaConvert statuses: SUBMITTED, PROGRESSING)
          const statusUpper = status.toUpperCase();
          if (statusUpper === "PROGRESSING" || statusUpper === "SUBMITTED") {
            new Promise(function (resolve) { setTimeout(resolve, 5000); })
              .then(() => {
                setRefreshVideoStatus(new Date().getTime().toString())
              })
              .catch(err => console.error("Video status polling error:", err));
          }
        })
        .catch(err => {
          console.error("Failed to get video status:", err);
          ToastHelper.error("Failed to check video processing status");
        });
    }
  }

  const handlePageRefresh = () => {
    init();
  }

  React.useEffect(init, [id]) // eslint-disable-line
  React.useEffect(loadTemplate, [message?.templateId]) // eslint-disable-line

  React.useEffect(() => { updateVideoStatus() }, [message?.awsJobId, refreshVideoStatus]);  // eslint-disable-line


  return (
    <div className="page-container">
      <div className="page-header">
        <h1><i className="far fa-image"></i> {message?.id > 0 ? "Edit Message" : "New Message"}</h1>
        <p className="page-subtitle">{template?.name || "Configure your message content and settings"}</p>
      </div>
      <div className="edit-layout">
        <div className="edit-form-column">
          {getVideoStatus()}
          <MessageEdit message={message} template={template} refreshSidebar={props.refreshSidebar} refreshPage={handlePageRefresh} pendingCommand={editCommand} sendCommand={handleMessageEditCommand} isLoading={isLoading || (videoStatus !== "" && videoStatus.toUpperCase() !== "ERROR" && videoStatus.toUpperCase() !== "COMPLETE" && videoStatus.toUpperCase() !== "CANCELED")} />
        </div>
        <div className="edit-preview-column">
          {getImageEditor()}
          <MessagePreview message={message} />
        </div>
      </div>
    </div>
  )


};
